import { lastValueFrom, take } from 'rxjs';

import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';

import { OperationsAPI } from '@services/API/trusts/operations/operation/operation.service';
import { CustomToastService } from '@services/custom-toast/custom-toast.service';
import { OperationsState } from '@services/state/operation-state/operation.state';

export const operationGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean> | boolean => {
  const operationsAPI = inject(OperationsAPI);
  const operationsState = inject(OperationsState);
  const customToastService = inject(CustomToastService);

  const trustNumber = next.params['operationId'].replace('-', '/');

  // Short-circuit: the state already holds this trust, so the pages behind the
  // guard (which read OperationsState rather than refetching) have what they
  // need. Returning synchronously also avoids re-running the paginated
  // getOperations chain when navigating between tabs of the same operation.
  const known = operationsState
    .get()
    .items.some(operation => operation.trust_number === trustNumber);

  if (known) {
    return true;
  }

  return lastValueFrom(
    operationsAPI.getOperations({ trust_number: [trustNumber] }).pipe(take(1))
  ).then(operations => {
    const operation = operations.items.find(
      operation => operation.trust_number === trustNumber
    );

    if (operation) {
      return true;
    }

    // All this guard knows is that the trust is absent from the portfolio's
    // operations. It used to announce `Facility created.`, which asserts a
    // cause it cannot observe: the same miss covers a removed facility, one
    // this user cannot see, and -- until the cache key started carrying
    // `urlWithParams` -- simply another facility's cached response, which is
    // how the message ended up in the middle of an ordinary navigation
    // (LEN-2158).
    //
    // Creating a facility never reaches this guard: `companies-mfe` navigates
    // to its own `['facility', res.id]` route afterwards, by facility id, not
    // by trust number. What does reach it is the user pressing "Go to
    // dashboard" on that page, possibly moments after creating one -- so the
    // wording keeps that reassurance without claiming it happened. A flag
    // passed through the navigation was tried and dropped: that button serves
    // a freshly created facility and a long-standing one identically, so
    // nothing there can set it truthfully.
    customToastService.showToast(
      `Facility not available in Portfolio Monitoring. If it was just created, it may take a moment to appear.`,
      { severity: 'warn', life: 4000 }
    );

    return false;
  });
};
